import { useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'

import { Box, HStack, Text } from '@chakra-ui/react'

import { EditIcon, IconButton, RemoveIcon, StatusPill } from '@hivewatch/dark'

import { EditTailgateGridButton } from '@/features/device-crud'
import { DeviceType } from '@/graphql/generated/schemas'

import { AssociatedDeviceRow, STATUS_CONFIG } from '../types/types'
import { colorTokens, getDeviceIcon } from '../utils/utils'

interface AssociatedDeviceIProps {
  row: AssociatedDeviceRow
  mainDeviceId: string
  mainDeviceName: string
  mainDeviceType: DeviceType
  shouldEnableAssociateDevices: boolean
  onRemove: (row: AssociatedDeviceRow) => void
  isRemoving?: boolean
}

export const AssociatedDevice = ({
  row,
  mainDeviceId,
  mainDeviceName,
  mainDeviceType,
  shouldEnableAssociateDevices,
  onRemove,
  isRemoving,
}: AssociatedDeviceIProps) => {
  const mode = useLightMode()
  const { bgColor, hoverBgColor, textColor } = colorTokens[mode]
  const [isHovered, setIsHovered] = useState(false)
  const { label, variant, icon, tooltip } = STATUS_CONFIG[row.status]

  const isMainCamera = mainDeviceType === DeviceType.Camera
  const doorId = isMainCamera ? row.id : mainDeviceId
  const doorName = isMainCamera ? row.name : mainDeviceName
  const cameraId = isMainCamera ? mainDeviceId : row.id
  const cameraName = isMainCamera ? mainDeviceName : row.name

  const canEditGrid =
    row.deviceType === DeviceType.Door || row.deviceType === DeviceType.Camera
  const shouldShowActions = shouldEnableAssociateDevices && isHovered
  const actionsMaxWidth = canEditGrid ? '72px' : '36px'

  return (
    <HStack
      _hover={{ bg: `bg.table-row-hover.${mode}` }}
      fontFamily='Soehne'
      justifyContent='space-between'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      px='24px'
      py='12px'
      transition='background-color 0.15s ease'
      w='full'
    >
      <HStack spacing='2'>
        {getDeviceIcon(row.deviceType, mode)}
        <Text fontSize='12px'>{row.name}</Text>
      </HStack>

      <HStack spacing='8px'>
        <StatusPill
          colorScheme={mode}
          icon={icon}
          label={label}
          tooltip={tooltip}
          variant={variant}
        />
        {shouldEnableAssociateDevices && (
          <Box
            overflow='hidden'
            sx={{
              maxWidth: shouldShowActions ? actionsMaxWidth : '0px',
              opacity: shouldShowActions ? 1 : 0,
              transition:
                'max-width 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease',
            }}
          >
            <HStack spacing='8px'>
              {canEditGrid && (
                <EditTailgateGridButton
                  cameraId={cameraId}
                  cameraName={cameraName}
                  colorScheme={mode}
                  connectionId={row.connectionId}
                  deviceType={row.deviceType}
                  doorId={doorId}
                  doorName={doorName}
                  overlayZIndex={1500}
                  renderTrigger={(onClick) => (
                    <IconButton
                      aria-label='Edit association'
                      bgColor={bgColor}
                      colorScheme={mode}
                      dimension={28}
                      hoverBgColor={hoverBgColor}
                      icon={<EditIcon size={16} />}
                      onClick={onClick}
                      textColor={textColor}
                    />
                  )}
                />
              )}
              <IconButton
                aria-label='Remove association'
                bgColor={bgColor}
                colorScheme={mode}
                dimension={28}
                disabled={isRemoving}
                hoverBgColor={hoverBgColor}
                icon={<RemoveIcon size={16} />}
                onClick={() => onRemove(row)}
                textColor={textColor}
              />
            </HStack>
          </Box>
        )}
      </HStack>
    </HStack>
  )
}
